import type { AxiosError } from 'axios';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  PasswordInput,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import type { PosSession } from 'washday-sdk';
import WashdayClientSingleton from '../../services/WashdayClientSingleton';
import { acceptSessionToken } from '../../services/sessionToken';

export default function ActiveSessions() {
  const { t, i18n } = useTranslation(['ProfilePage']);
  const userId = useSelector(
    (state: { user: { user?: { _id?: string } } }) => state.user.user?._id
  );
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const queryKey = ['auth', 'sessions', userId];
  const [target, setTarget] = useState<PosSession | 'others' | null>(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [success, setSuccess] = useState(false);
  const sessions = useQuery({
    queryKey,
    enabled: Boolean(userId),
    retry: false,
    refetchOnWindowFocus: true,
    meta: { silent: true },
    queryFn: async () => {
      const token = Cookies.get('token');
      const response = await WashdayClientSingleton.getInstance().auth.getSessions();
      acceptSessionToken(response.data.data.token, token, dispatch);
      return response.data.data;
    }
  });
  const close = () => {
    if (!revoke.isPending) {
      setTarget(null);
      setPassword('');
      setPasswordError('');
    }
  };
  const revoke = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: async () => {
      const token = Cookies.get('token');
      const auth = WashdayClientSingleton.getInstance().auth;
      const response =
        target === 'others'
          ? await auth.revokeOtherSessions({ password })
          : await auth.revokeSession((target as PosSession).sid, { password });
      acceptSessionToken(response.data.data.token, token, dispatch);
      return response.data.data;
    },
    onSuccess: () => {
      setTarget(null);
      setPassword('');
      setPasswordError('');
      setSuccess(true);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: unknown) => {
      const data = (error as AxiosError<{ code?: string; error?: { code?: string } }>)?.response
        ?.data;
      const code = data?.code || data?.error?.code;
      setPasswordError(
        t(
          code === 'PASSWORD_INVALID'
            ? 'ProfilePage:sessions.passwordInvalid'
            : 'ProfilePage:sessions.revokeError'
        )
      );
    }
  });
  const open = (session: PosSession | 'others') => {
    setTarget(session);
    setPassword('');
    setPasswordError('');
    setSuccess(false);
  };
  const date = (value: string) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? t('ProfilePage:sessions.unknown')
      : parsed.toLocaleString(i18n.language);
  };

  return (
    <Card mt="xl" withBorder>
      <Stack>
        <Title order={3}>{t('ProfilePage:sessions.security')}</Title>
        <Group justify="space-between">
          <Title order={4}>{t('ProfilePage:sessions.title')}</Title>
          <Button
            type="button"
            variant="light"
            color="red"
            disabled={!sessions.isSuccess || sessions.isFetching || revoke.isPending}
            onClick={() => open('others')}
          >
            {t('ProfilePage:sessions.revokeOthers')}
          </Button>
        </Group>
        <Alert color="blue">{t('ProfilePage:sessions.offline')}</Alert>
        {success && (
          <Alert color="green" role="status">
            {t('ProfilePage:sessions.success')}
          </Alert>
        )}
        {sessions.isPending && (
          <Group role="status">
            <Loader size="sm" />
            <Text>{t('ProfilePage:sessions.loading')}</Text>
          </Group>
        )}
        {sessions.isError && (
          <Alert color="red">
            <Text>{t('ProfilePage:sessions.loadError')}</Text>
            <Button mt="sm" type="button" onClick={() => void sessions.refetch()}>
              {t('ProfilePage:sessions.retry')}
            </Button>
          </Alert>
        )}
        {sessions.isSuccess && !sessions.data.sessions.length && (
          <Text>{t('ProfilePage:sessions.empty')}</Text>
        )}
        {!sessions.isError &&
          sessions.data?.sessions.map((session) => (
            <Card key={session.sid} withBorder>
              <Group justify="space-between" align="start" wrap="wrap">
                <Stack gap={4}>
                  <Group>
                    <Text fw={600}>
                      {[session.platform, session.device, session.browser]
                        .filter(Boolean)
                        .join(' · ') || t('ProfilePage:sessions.unknown')}
                    </Text>
                    {session.isCurrent && <Badge>{t('ProfilePage:sessions.current')}</Badge>}
                  </Group>
                  <Text size="sm">
                    {t('ProfilePage:sessions.created')}: {date(session.createdAt)}
                  </Text>
                  <Text size="sm">
                    {t('ProfilePage:sessions.lastSeen')}: {date(session.lastSeenAt)}
                  </Text>
                </Stack>
                {!session.isCurrent && (
                  <Button
                    type="button"
                    color="red"
                    variant="subtle"
                    disabled={revoke.isPending || sessions.isFetching}
                    onClick={() => open(session)}
                  >
                    {t('ProfilePage:sessions.revoke')}
                  </Button>
                )}
              </Group>
            </Card>
          ))}
        {sessions.data?.hasMore && <Text size="sm">{t('ProfilePage:sessions.more')}</Text>}
      </Stack>
      <Modal
        opened={target !== null}
        onClose={close}
        title={t(
          target === 'others'
            ? 'ProfilePage:sessions.confirmOthers'
            : 'ProfilePage:sessions.confirmSingle'
        )}
        closeOnClickOutside={!revoke.isPending}
        closeOnEscape={!revoke.isPending}
        withCloseButton={!revoke.isPending}
      >
        <form
          data-testid="session-password-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (revoke.isPending) return;
            if (!password) {
              setPasswordError(t('ProfilePage:sessions.passwordRequired'));
              return;
            }
            if (navigator.onLine === false) {
              setPasswordError(t('ProfilePage:sessions.revokeError'));
              return;
            }
            revoke.mutate();
          }}
        >
          <Stack>
            <Text size="sm">{t('ProfilePage:sessions.confirmDescription')}</Text>
            <PasswordInput
              autoFocus
              autoComplete="current-password"
              label={t('ProfilePage:sessions.password')}
              value={password}
              disabled={revoke.isPending}
              onChange={(event) => {
                setPassword(event.currentTarget.value);
                setPasswordError('');
              }}
              error={passwordError || undefined}
            />
            <Group justify="flex-end">
              <Button type="button" variant="default" onClick={close} disabled={revoke.isPending}>
                {t('ProfilePage:sessions.cancel')}
              </Button>
              <Button type="submit" color="red" loading={revoke.isPending}>
                {t('ProfilePage:sessions.confirm')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Card>
  );
}
